// Libs
import classNames from 'classnames/bind';
import TimelineItem from '@mui/lab/TimelineItem';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
  TimelineSeparator,
} from '@mui/lab';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
// Components, Layouts, Pages
// Others
import { IActivity } from '~/utils/interface/activity';
import { ActivityTypeEnum, ActivityTypeIconEnum, DateFormatEnum } from '~/utils/enum/common';
import { formatDate, formatTime, getFullName, toCamelCase } from '~/utils/helper/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseTimelineItem.module.scss';
import { icons } from '~/assets';

type Props = {
  activityData: IActivity[];
  date?: string;
  title?: string;
  desc?: string;
  onView?: (activity: IActivity) => void;
};

const cx = classNames.bind(styles);

const BaseTimelineItem = (props: Props) => {
  //#region Destructuring Props
  const { activityData, onView } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const groupedActivities = useMemo(() => {
    const grouped: Record<string, typeof activityData> = {};

    activityData.forEach((activity) => {
      const activityDate = formatDate(activity.createdAt, DateFormatEnum.YYYY_MM_DD);
      if (!grouped[activityDate]) {
        grouped[activityDate] = [];
      }
      grouped[activityDate].push(activity);
    });

    return Object.entries(grouped).map(([date, activityList]) => ({
      date,
      activityList,
    }));
  }, [activityData]);

  const handleViewDetail = (activity: IActivity) => {
    if (!activity) return;

    onView && onView(activity);
  };

  const renderIcon = (actionType: string) => {
    let icon;
    let actionIconName;

    switch (true) {
      case actionType?.includes(ActivityTypeIconEnum.CREATED):
        icon = icons.activityIconCreate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.CREATED);
        break;

      case actionType?.includes(ActivityTypeIconEnum.UPDATED):
        icon = icons.activityIconUpdate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.UPDATED);
        break;

      case actionType?.includes(ActivityTypeIconEnum.DELETED):
        icon = icons.activityIconDelete;
        actionIconName = toCamelCase(ActivityTypeIconEnum.DELETED);
        break;

      case actionType?.includes(ActivityTypeIconEnum.UPLOADED):
        icon = icons.activityIconUpdate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.UPLOADED);
        break;

      case actionType?.includes(ActivityTypeIconEnum.CHANGE_STATUS):
        icon = icons.activityIconUpdate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.CHANGE_STATUS);
        break;

      case actionType?.includes(ActivityTypeIconEnum.ASSIGNED):
        icon = icons.activityIconUpdate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.ASSIGNED);
        break;

      case actionType?.includes(ActivityTypeIconEnum.CONVERT):
        icon = icons.activityIconUpdate;
        actionIconName = toCamelCase(ActivityTypeIconEnum.CONVERT);
        break;

      default:
        break;
    }

    return (
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'center'}
        className={cx('timelineIconGroup', actionIconName)}
      >
        <img src={icon} alt={t('common_alt_img')} className={cx('timelineIcon')} />
      </Stack>
    );
  };

  const renderTitle = (actionType: string) => {
    switch (actionType) {
      case ActivityTypeEnum.ASSIGNED_JOB:
        return t('activity_title_assigned_job');

      case ActivityTypeEnum.ASSIGNED_SCHEDULE:
        return t('activity_title_assigned_schedule');

      case ActivityTypeEnum.CHANGE_STATUS_CONTACT:
        return t('activity_title_change_status_contact');

      case ActivityTypeEnum.CHANGE_STATUS_CUSTOMER:
        return t('activity_title_change_status_customer');

      case ActivityTypeEnum.CREATE_CUSTOMER:
        return t('activity_title_create_customer');

      case ActivityTypeEnum.CREATE_FOLDER:
        return t('activity_title_create_folder');

      case ActivityTypeEnum.DELETE_FILE:
        return t('activity_title_delete_file');

      case ActivityTypeEnum.DELETE_FOLDER:
        return t('activity_title_delete_folder');

      case ActivityTypeEnum.UPDATE_CUSTOMER:
        return t('activity_title_update_customer');

      case ActivityTypeEnum.UPLOAD_FILE:
        return t('activity_title_upload_file');

      case ActivityTypeEnum.CREATE_JOB:
        return t('activity_title_create_job');

      case ActivityTypeEnum.CREATE_NOTE:
        return t('activity_title_create_note');

      case ActivityTypeEnum.UPDATE_NOTE:
        return t('activity_title_update_note');

      case ActivityTypeEnum.DELETE_NOTE:
        return t('activity_title_delete_note');

      case ActivityTypeEnum.CREATE_ESTIMATE:
        return t('activity_title_create_estimate');

      case ActivityTypeEnum.CHANGE_STATUS_ESTIMATE:
        return t('activity_title_change_status_estimate');

      case ActivityTypeEnum.CONVERT_ESTIMATE_TO_JOB:
        return t('activity_title_convert_to_job_estimate');

      case ActivityTypeEnum.UPDATE_CUSTOMER_ESTIMATE:
      case ActivityTypeEnum.UPDATE_SCHEDULE_ESTIMATE:
      case ActivityTypeEnum.UPDATE_LINE_ITEM_ESTIMATE:
      case ActivityTypeEnum.UPDATE_PRIVATE_NOTES_ESTIMATE:
      case ActivityTypeEnum.UPDATE_ESTIMATE_FIELDS:
      case ActivityTypeEnum.UPDATE_ESTIMATE_TAGS:
      case ActivityTypeEnum.UPDATE_LEAD_SOURCE_ESTIMATE:
        return t('activity_title_update_estimate');

      case ActivityTypeEnum.UPDATE_CUSTOMER_JOB:
      case ActivityTypeEnum.UPDATE_SCHEDULE_JOB:
      case ActivityTypeEnum.UPDATE_LINE_ITEM_JOB:
      case ActivityTypeEnum.UPDATE_PRIVATE_NOTES_JOB:
      case ActivityTypeEnum.UPDATE_JOB_FIELDS:
      case ActivityTypeEnum.UPDATE_JOB_TAGS:
      case ActivityTypeEnum.UPDATE_LEAD_SOURCE_JOB:
      case ActivityTypeEnum.UPDATE_TASKS:
        return t('activity_title_update_job');

      default:
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Timeline
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.1,
        },
      }}
      className={cx('timeline')}
    >
      {Array.isArray(groupedActivities) &&
        groupedActivities?.map(({ date, activityList }) => (
          <Box key={date}>
            <TimelineItem>
              <TimelineOppositeContent
                className={cx('timelineOPpositeContent')}
                align='right'
                variant='body2'
                color='text.secondary'
              >
                <Stack
                  className={cx('timelineDate')}
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  {formatDate(date)}
                </Stack>
              </TimelineOppositeContent>

              <TimelineSeparator className={cx('timelineSeparator')}>
                <TimelineDot variant='filled' color='primary'></TimelineDot>

                <TimelineConnector />
              </TimelineSeparator>

              <TimelineContent className={cx('timelineContent')}></TimelineContent>
            </TimelineItem>

            {activityList?.map((activity, index) => (
              <TimelineItem key={activity?.id}>
                <TimelineOppositeContent
                  className={cx('timelineOppositeContentSecond')}
                  align='right'
                  variant='body2'
                  color='text.secondary'
                >
                  <Stack
                    className={cx('timeLineTime')}
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                  >
                    {formatTime(activity?.createdAt)}
                  </Stack>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot className={cx('timelineImg')}>
                    {activity?.actionType ? renderIcon(activity?.actionType) : EMPTY_STRING}
                  </TimelineDot>

                  {index !== activityList.length - 1 && (
                    <TimelineConnector className={cx('timelineConnector')} />
                  )}
                </TimelineSeparator>

                <TimelineContent className={cx('timelineContent')}>
                  <Typography variant='h6' component='span' className={cx('timelineContentTitle')}>
                    {renderTitle(activity?.actionType)}
                  </Typography>

                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={'24px'}
                    className={cx('timelineViewDetailGroup')}
                  >
                    <Typography className={cx('timelineContentDesc')}>
                      {t('activity_created_by', { name: getFullName({ ...activity?.createdBy }) })}
                    </Typography>

                    <Link
                      component={'button'}
                      variant='body2'
                      underline='none'
                      onClick={() => handleViewDetail(activity)}
                      className={cx('timelineContentViewDetails')}
                    >
                      {t('common_action_view_details')}
                    </Link>
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Box>
        ))}
    </Timeline>
  );
};

export default BaseTimelineItem;
